export const REASON_CODES = [
  'INVALID_CODE',
  'UNKNOWN_CODE',
  'NOT_RECEIVED',
  'VOIDED',
  'ALREADY_SHIPPED',
  'OTHER_SITE',
  'IN_TRANSIT',
  'WRONG_SKU',
  'STICKER_SKU_MISMATCH',
  'SKU_REQUIRED',
  'LINE_COMPLETE',
  'PICKED_ELSEWHERE',
  'RESERVED_FOR_ORDERS',
  'NOT_IN_TRANSFER',
  'WRITTEN_OFF',
  'ALREADY_RECEIVED',
  'ALREADY_SCANNED',
  'NOT_IN_COUNT',
  'PALLET_NOT_ALLOWED',
  'TEST_STICKER',
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];

export type ReasonVars = Partial<Record<'sku' | 'expectedSku' | 'neededSkus' | 'site' | 'date' | 'ref' | 'transfer' | 'n' | 'reason', string>>;

const SEVERITY: Partial<Record<ReasonCode, 'duplicate' | 'ignored'>> = {
  ALREADY_RECEIVED: 'duplicate',
  ALREADY_SCANNED: 'duplicate',
  NOT_IN_COUNT: 'ignored',
};

export function reasonSeverity(code: ReasonCode): 'duplicate' | 'ignored' | 'rejected' {
  return SEVERITY[code] ?? 'rejected';
}

const or = (v: string | undefined, fallback: string) => (v && v.length ? v : fallback);

const TEMPLATES: Record<ReasonCode, (v: ReasonVars) => string> = {
  INVALID_CODE: () => "This doesn't look like one of our stickers",
  UNKNOWN_CODE: () => "This sticker isn't in the system",
  NOT_RECEIVED: () => 'Never received. Scan it in Receive first',
  VOIDED: (v) => (v.date ? `This sticker was voided on ${v.date}` : 'This sticker was voided'),
  ALREADY_SHIPPED: (v) => `Shipped ${or(v.date, 'earlier')} on order ${or(v.ref, '(unknown)')}`,
  OTHER_SITE: (v) => `This box is recorded at ${or(v.site, 'another site')}`,
  IN_TRANSIT: (v) => `In transit to ${or(v.site, 'another site')} (transfer ${or(v.transfer, '?')})`,
  WRONG_SKU: (v) => `This is ${or(v.sku, 'a different SKU')}. Order needs ${or(v.neededSkus, 'other SKUs')}`,
  STICKER_SKU_MISMATCH: (v) => `This sticker is ${or(v.sku, 'another SKU')}, you are receiving ${or(v.expectedSku, 'a different SKU')}`,
  SKU_REQUIRED: () => 'Blank sticker. Choose the SKU you are receiving first',
  LINE_COMPLETE: (v) => `Already have ${or(v.n, 'all')} / ${or(v.n, 'all')} ${or(v.sku, '')}`.trim(),
  PICKED_ELSEWHERE: (v) => `Already scanned for ${or(v.ref, 'another order')}`,
  RESERVED_FOR_ORDERS: (v) => `All remaining ${or(v.sku, 'boxes of this SKU')} here are reserved for orders`,
  NOT_IN_TRANSFER: (v) => `This box isn't part of transfer ${or(v.transfer, '?')}`,
  WRITTEN_OFF: (v) => `Written off ${or(v.date, 'earlier')}: ${or(v.reason, 'no reason given')}`,
  ALREADY_RECEIVED: () => 'Already received',
  ALREADY_SCANNED: () => 'Already scanned',
  NOT_IN_COUNT: (v) => `Not in this count: ${or(v.sku, 'other SKU')}, ignored`,
  PALLET_NOT_ALLOWED: () => 'Scan box stickers here, not pallet stickers',
  TEST_STICKER: () => 'This is a test sticker, not a box.',
};

export function reasonMessage(code: ReasonCode, vars: ReasonVars = {}): string {
  return TEMPLATES[code](vars);
}
